import { useState, useEffect, useCallback } from 'react'
import { showToast } from '@/features/automation/lib/toast'
import type { Company, Website, ScrapeJob, DocumentUploadJob, UploadedFile } from '@/features/automation/types'
import {
  getAllCompanies,
  createCompany,
  deleteCompany,
  getAllWebsites,
  createWebsite,
  deleteWebsite,
} from '@/features/automation/repository/companies.repository'
import { getScrapeJobs } from '@/features/automation/repository/scrape-jobs.repository'
import {
  getDocumentUploadJobs,
  getUploadedFiles,
  deleteUploadedFile,
} from '@/features/automation/repository/document-upload.repository'
import { triggerScrape } from '@/features/automation/api/scraping.api'
import {
  uploadDocument,
  fileToBase64,
  getFileExtension,
  isSupportedFileType,
  SUPPORTED_FILE_TYPES,
} from '@/features/automation/api/document-upload.api'
import { usePolling, hasActiveStatus } from '@/features/automation/lib/hooks'
import { CompanySelector } from '@/features/automation/ui/company-selector'
import { WebsiteScraping } from '@/features/automation/ui/website-scraping'
import { DocumentUploadSection } from '@/features/automation/ui/document-upload-section'
import { JobStatusPanels } from '@/features/automation/ui/job-status-panels'

export function AutomationManager() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [selectedCompany, setSelectedCompany] = useState<string>('')
  const [websites, setWebsites] = useState<Website[]>([])
  const [jobs, setJobs] = useState<ScrapeJob[]>([])
  const [uploadJobs, setUploadJobs] = useState<DocumentUploadJob[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [scrapePollingEnabled, setScrapePollingEnabled] = useState(false)
  const [uploadPollingEnabled, setUploadPollingEnabled] = useState(false)

  const fetchScrapeJobs = useCallback(
    () => getScrapeJobs(selectedCompany),
    [selectedCompany]
  )

  const fetchUploadJobs = useCallback(
    () => getDocumentUploadJobs(selectedCompany),
    [selectedCompany]
  )

  const { isPolling: isScrapePolling } = usePolling<ScrapeJob[]>({
    fetcher: fetchScrapeJobs,
    interval: 3000,
    enabled: scrapePollingEnabled && !!selectedCompany,
    onSuccess: (data) => {
      setJobs(data)
      if (!hasActiveStatus(data, ['pending', 'running'])) {
        setScrapePollingEnabled(false)
      }
    },
    onError: (error) => {
      showToast.error({ title: 'Scrape polling failed', description: error.message })
      setScrapePollingEnabled(false)
    },
  })

  const { isPolling: isUploadPolling } = usePolling<DocumentUploadJob[]>({
    fetcher: fetchUploadJobs,
    interval: 3000,
    enabled: uploadPollingEnabled && !!selectedCompany,
    onSuccess: async (data) => {
      setUploadJobs(data)
      if (!hasActiveStatus(data, ['pending', 'running'])) {
        setUploadPollingEnabled(false)
        if (selectedCompany) {
          const files = await getUploadedFiles(selectedCompany)
          setUploadedFiles(files)
        }
      }
    },
    onError: (error) => {
      showToast.error({ title: 'Upload polling failed', description: error.message })
      setUploadPollingEnabled(false)
    },
  })

  useEffect(() => {
    loadCompanies()
  }, [])

  useEffect(() => {
    if (selectedCompany) {
      loadWebsites(selectedCompany)
      loadJobs(selectedCompany)
      loadUploadJobs(selectedCompany)
      loadUploadedFiles(selectedCompany)
    }
  }, [selectedCompany])

  async function loadCompanies() {
    try {
      const data = await getAllCompanies()
      setCompanies(data)
    } catch (error) {
      showToast.error({ title: 'Failed to load companies', description: (error as Error).message })
    }
  }

  async function loadWebsites(companyId: string) {
    try {
      const data = await getAllWebsites(companyId)
      setWebsites(data)
    } catch (error) {
      showToast.error({ title: 'Failed to load websites', description: (error as Error).message })
    }
  }

  async function loadJobs(companyId: string) {
    try {
      const data = await getScrapeJobs(companyId, 10)
      setJobs(data)
    } catch (error) {
      showToast.error({ title: 'Failed to load jobs', description: (error as Error).message })
    }
  }

  async function loadUploadJobs(companyId: string) {
    try {
      const data = await getDocumentUploadJobs(companyId, 10)
      setUploadJobs(data)
    } catch (error) {
      console.error('Failed to load upload jobs:', error)
    }
  }

  async function loadUploadedFiles(companyId: string) {
    try {
      const data = await getUploadedFiles(companyId)
      setUploadedFiles(data)
    } catch (error) {
      console.error('Failed to load uploaded files:', error)
    }
  }

  async function handleCreateCompany(name: string) {
    try {
      const company = await createCompany(name)
      showToast.success('Company created')
      loadCompanies()
      setSelectedCompany(company.id)
    } catch (error) {
      showToast.error({ title: 'Failed to create company', description: (error as Error).message })
    }
  }

  async function handleAddWebsite(url: string) {
    if (!selectedCompany) return
    try {
      await createWebsite(selectedCompany, url)
      showToast.success('Website added')
      loadWebsites(selectedCompany)
    } catch (error) {
      showToast.error({ title: 'Failed to add website', description: (error as Error).message })
    }
  }

  async function handleDeleteWebsite(websiteId: string) {
    try {
      await deleteWebsite(websiteId)
      showToast.success('Website deleted')
      loadWebsites(selectedCompany)
    } catch (error) {
      showToast.error({ title: 'Failed to delete website', description: (error as Error).message })
    }
  }

  async function handleStartScraping() {
    if (!selectedCompany) return

    const company = companies.find((c) => c.id === selectedCompany)
    if (!company) return

    if (websites.length === 0) {
      showToast.error('Add at least one website URL before scraping')
      return
    }

    setLoading(true)
    try {
      await triggerScrape({
        companyName: company.name,
        companyId: company.id,
        maxResults: 10,
        websites: websites.map((w) => w.url),
      })
      showToast.success({ title: 'Scraping started', description: 'Status will update automatically' })
      await loadJobs(selectedCompany)
      setScrapePollingEnabled(true)
    } catch (error) {
      showToast.error({ title: 'Failed to start scraping', description: (error as Error).message })
    } finally {
      setLoading(false)
    }
  }

  async function handleFilesSelected(files: File[]) {
    if (!selectedCompany) return

    const company = companies.find((c) => c.id === selectedCompany)
    if (!company) return

    setUploadLoading(true)

    for (const file of files) {
      const extension = getFileExtension(file.name)

      if (!isSupportedFileType(extension)) {
        showToast.error({ title: 'Unsupported file type', description: `${extension} is not supported. Use: ${SUPPORTED_FILE_TYPES.join(', ')}` })
        continue
      }

      try {
        const base64Content = await fileToBase64(file)

        await uploadDocument({
          companyName: company.name,
          companyId: company.id,
          filename: file.name,
          fileType: extension,
          fileContent: base64Content,
          fileSizeBytes: file.size,
        })

        showToast.success({ title: 'File uploaded', description: file.name })
        await loadUploadJobs(selectedCompany)
      } catch (error) {
        showToast.error({ title: `Failed to upload ${file.name}`, description: (error as Error).message })
      }
    }

    setUploadLoading(false)
    setUploadPollingEnabled(true)
  }

  async function handleDeleteFile(filename: string) {
    if (!selectedCompany) return

    const confirmed = confirm(`Are you sure you want to delete "${filename}"?`)
    if (!confirmed) return

    try {
      await deleteUploadedFile(selectedCompany, filename)
      showToast.success({ title: 'File deleted', description: filename })
      loadUploadedFiles(selectedCompany)
    } catch (error) {
      showToast.error({ title: 'Failed to delete file', description: (error as Error).message })
    }
  }

  async function handleDeleteCompany() {
    if (!selectedCompany) return

    const confirmed = confirm('Are you sure? This will delete all company data including websites and content.')
    if (!confirmed) return

    try {
      await deleteCompany(selectedCompany)
      showToast.success('Company deleted')
      setSelectedCompany('')
      loadCompanies()
    } catch (error) {
      showToast.error({ title: 'Failed to delete company', description: (error as Error).message })
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-theme-primary mb-6">Automation</h1>

      <CompanySelector
        companies={companies}
        selectedCompany={selectedCompany}
        onSelectCompany={setSelectedCompany}
        onCreateCompany={handleCreateCompany}
        onDeleteCompany={handleDeleteCompany}
      />

      {selectedCompany && (
        <>
          <WebsiteScraping
            websites={websites}
            loading={loading}
            onAddWebsite={handleAddWebsite}
            onDeleteWebsite={handleDeleteWebsite}
            onStartScraping={handleStartScraping}
          />

          <DocumentUploadSection
            uploadedFiles={uploadedFiles}
            uploadLoading={uploadLoading}
            onFilesSelected={handleFilesSelected}
            onDeleteFile={handleDeleteFile}
          />

          <JobStatusPanels
            scrapeJobs={jobs}
            uploadJobs={uploadJobs}
            isScrapePolling={isScrapePolling}
            isUploadPolling={isUploadPolling}
          />
        </>
      )}
    </div>
  )
}
